import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { mobileTokenLogin } from "../../api";

const getSafeRedirect = (redirectUrl) => {
  if (!redirectUrl) return "/config";
  const urlStr = String(redirectUrl).trim();
  if (!urlStr.startsWith("/")) return "/config";
  if (urlStr.startsWith("//")) return "/config";
  return urlStr;
};

const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") return null;

    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getFirstProjectId = (userData) => {
  const accesses = Array.isArray(userData?.accesses) ? userData.accesses : [];
  const activeAccess =
    accesses.find((item) => item?.active && item?.project_id) ||
    accesses.find((item) => item?.project_id);

  return activeAccess?.project_id || "";
};

const getFirstRole = (userData) => {
  const roles = Array.isArray(userData?.roles) ? userData.roles : [];
  return roles[0] || "";
};

const SESSION_KEYS = [
  "ACCESS_TOKEN",
  "access",
  "access_token",
  "accessToken",
  "token",
  "TOKEN",
  "authToken",
  "REFRESH_TOKEN",
  "refresh",
  "refresh_token",
  "refreshToken",
  "USER_DATA",
  "user",
  "currentUser",
  "userData",
  "authUser",
  "USER_ID",
  "user_id",
  "ORG_ID",
  "org_id",
  "organization_id",
  "ACTIVE_PROJECT_ID",
  "PROJECT_ID",
  "project_id",
  "active_project_id",
  "ACTIVE_PROJECT_NAME",
  "PROJECT_NAME",
  "ROLE",
  "FLOW_ROLE",
  "MOBILE_WEBVIEW_SESSION",
];

const clearStaleSession = () => {
  SESSION_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const persistMobileLoginSession = ({ access, refresh, userData, mobileContext }) => {
  if (!access) return false;

  const decodedUserData = decodeJwtPayload(access) || {};
  const finalUserData = {
    ...decodedUserData,
    ...(userData || {}),
  };

  // Token storage
  localStorage.setItem("ACCESS_TOKEN", access);
  localStorage.setItem("access", access);
  localStorage.setItem("access_token", access);
  localStorage.setItem("accessToken", access);
  localStorage.setItem("token", access);

  if (refresh) {
    localStorage.setItem("REFRESH_TOKEN", refresh);
    localStorage.setItem("refresh", refresh);
    localStorage.setItem("refresh_token", refresh);
    localStorage.setItem("refreshToken", refresh);
  }

  // User payload storage
  localStorage.setItem("USER_DATA", JSON.stringify(finalUserData));
  localStorage.setItem("user", JSON.stringify(finalUserData));
  localStorage.setItem("currentUser", JSON.stringify(finalUserData));
  localStorage.setItem("userData", JSON.stringify(finalUserData));

  const userId = finalUserData?.user_id || finalUserData?.id || "";
  if (userId) {
    localStorage.setItem("USER_ID", String(userId));
    localStorage.setItem("user_id", String(userId));
  }

  const orgId =
    finalUserData?.org ||
    finalUserData?.org_id ||
    finalUserData?.organization_id ||
    "";
  if (orgId) {
    localStorage.setItem("ORG_ID", String(orgId));
    localStorage.setItem("org_id", String(orgId));
    localStorage.setItem("organization_id", String(orgId));
  }

  // Mobile Context overrides (Authoritative Project ID & Role)
  const projectId =
    mobileContext?.project_id ||
    getFirstProjectId(finalUserData) ||
    finalUserData?.project_id ||
    "";

  if (projectId) {
    localStorage.setItem("ACTIVE_PROJECT_ID", String(projectId));
    localStorage.setItem("PROJECT_ID", String(projectId));
    localStorage.setItem("project_id", String(projectId));
    localStorage.setItem("active_project_id", String(projectId));
  }

  const role = mobileContext?.role || getFirstRole(finalUserData);
  if (role) {
    const normalizedRole = String(role)
      .trim()
      .toUpperCase()
      .replace(/[-\s]+/g, "_");
    localStorage.setItem("ROLE", normalizedRole);
    localStorage.setItem("FLOW_ROLE", normalizedRole);
  }

  localStorage.setItem("MOBILE_WEBVIEW_SESSION", "true");

  return true;
};

const hasUsableSession = () => {
  const access =
    localStorage.getItem("ACCESS_TOKEN") ||
    localStorage.getItem("access") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token");

  const rawUser = localStorage.getItem("USER_DATA");
  let hasUser = false;
  if (rawUser && rawUser !== "undefined" && rawUser !== "null") {
    try {
      const u = JSON.parse(rawUser);
      hasUser = Boolean(u?.id || u?.user_id);
    } catch {
      hasUser = false;
    }
  }

  return Boolean(access && hasUser);
};

const MobileLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    const oneTimeToken = searchParams.get("token");
    const queryRedirect = searchParams.get("redirect");

    // If oneTimeToken is present in URL, ALWAYS invalidate existing session and exchange token
    if (oneTimeToken) {
      if (exchangeStartedRef.current) return;
      exchangeStartedRef.current = true;

      // Invalidate existing session unconditionally
      clearStaleSession();

      const doMobileLogin = async () => {
        try {
          const response = await mobileTokenLogin(oneTimeToken);
          const data = response?.data || {};

          if (!data.access) {
            setError("Mobile login failed. Access token missing.");
            setLoading(false);
            return;
          }

          const mobileContext = data.mobile_context || {};
          const saved = persistMobileLoginSession({
            access: data.access,
            refresh: data.refresh,
            userData: data.user_data || data.user || data.profile || null,
            mobileContext,
          });

          if (!saved) {
            setError("Mobile login failed. Could not save session.");
            setLoading(false);
            return;
          }

          const redirectTarget = getSafeRedirect(
            mobileContext.redirect || queryRedirect
          );

          // Force full page reload to ensure fresh initialization of layout and API clients
          window.location.replace(redirectTarget);
        } catch (err) {
          console.error("Mobile login exchange failed:", err);

          setError(
            err?.response?.data?.detail ||
              err?.response?.data?.message ||
              err?.response?.data?.token?.[0] ||
              "Mobile login failed. One-time token is invalid or expired."
          );
          setLoading(false);
        }
      };

      doMobileLogin();
      return;
    }

    // No one-time token in URL
    if (hasUsableSession()) {
      const redirectTarget = getSafeRedirect(queryRedirect);
      navigate(redirectTarget, { replace: true });
      return;
    }

    setError("Mobile login token is missing.");
    setLoading(false);
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <h3 style={styles.title}>Logging you in...</h3>
          <p style={styles.text}>Please wait while we open your workspace.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <h3 style={styles.title}>Login Failed</h3>
          <p style={styles.error}>{error}</p>
        </div>
      </div>
    );
  }

  return null;
};

const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    background: "#fcfaf7",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    padding: 24,
    borderRadius: 12,
    background: "#ffffff",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  title: {
    margin: "0 0 8px",
    fontSize: 22,
    fontWeight: 700,
  },
  text: {
    margin: 0,
    fontSize: 14,
    color: "#555",
  },
  error: {
    margin: 0,
    fontSize: 14,
    color: "#c62828",
  },
};

export default MobileLogin;